import cn from 'clsx'
import React from 'react'
import { useDrop } from 'react-dnd'

import { FILE, TRACK } from '@disco/constants/ItemTypes'
import { appendTracksToPlaylist } from '@disco/state-management/actions/ExplorerActions'
import { uploadFilesToPlaylist } from '@disco/state-management/actions/upload-actions'
import { getAllSelectedItems } from '@disco/state-management/selectors/selection'
import { getFilesFromDraggedItem } from '@disco/utils/file-utils'
import { isDraggedItemPartOfSelection } from '@disco/utils/selection'
import { isReadOnlyTrack } from '@disco/utils/tracks'

import type { Playlist, PlaylistId } from 'types/playlist'
import type { Track } from 'types/track'

import { dndConnectRef } from 'utils/dnd-connect-ref'

import { DropFilesIndicator } from 'components/ui/drop-indicator'

import { useDispatch, useSelector } from 'hooks/use-redux'
import useSpringLoad from 'hooks/use-spring-load'

type DraggedItem = {
  track?: Track
  files?: File[]
  items?: DataTransferItemList
  sourcePlaylistId?: PlaylistId
  [key: string]: unknown
}

type Props = {
  playlist: Playlist
  children?: React.ReactNode
  openPane?: () => void
  onDrop?: (playlist: Playlist, draggedItem: DraggedItem) => unknown
}

export default function PlaylistDropWrapper({
  playlist,
  children,
  openPane,
  onDrop,
}: Props) {
  const dispatch = useDispatch()
  const selectedItems = useSelector(getAllSelectedItems)

  const [{ isOver, canDrop, draggedItem }, dropRef] = useDrop<
    DraggedItem,
    unknown,
    { isOver: boolean; canDrop: boolean; draggedItem: DraggedItem | null }
  >(
    () => ({
      accept: [TRACK, FILE],
      canDrop: item => {
        if (playlist.playlist_id === item?.sourcePlaylistId) {
          return false
        }
        if (item?.track) {
          const readOnlyTrack = isReadOnlyTrack(item.track)
          return Boolean(item.track.uploaded) && !readOnlyTrack
        }
        if (item?.files) {
          return true
        }
        return false
      },
      drop: (item, monitor) => {
        if (item?.sourcePlaylistId === playlist.playlist_id) {
          return
        }
        if (monitor.didDrop()) {
          return
        }

        if (onDrop) {
          onDrop(playlist, item)
          return
        }

        if (item?.items) {
          getFilesFromDraggedItem({ items: item.items }).then(
            (files: File[]) => {
              if (files.length > 0) {
                dispatch(uploadFilesToPlaylist(files, playlist))
              }
            },
          )
        }

        if (item?.track && item.track.uploaded) {
          const trackSelected =
            selectedItems.length > 0 &&
            isDraggedItemPartOfSelection(selectedItems, item)

          if (!trackSelected) {
            dispatch(appendTracksToPlaylist(playlist, [item.track]))
          } else {
            const tracks = selectedItems.filter(
              (i): i is Track => i.type === TRACK,
            )
            if (tracks.length > 0) {
              dispatch(appendTracksToPlaylist(playlist, tracks))
            }
          }
        }
      },
      collect: monitor => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
        draggedItem: monitor.getItem(),
      }),
    }),
    [dispatch, playlist, onDrop, selectedItems],
  )

  const willOpen = useSpringLoad(
    Boolean(isOver && canDrop && openPane),
    openPane,
  )

  return (
    <div
      ref={dndConnectRef(dropRef)}
      className={cn({
        HighlightedDropTarget: isOver && canDrop,
        'HighlightedDropTarget--blink': willOpen,
      })}
    >
      {children}
      {isOver && draggedItem?.files && <DropFilesIndicator />}
    </div>
  )
}
